import { motion } from "framer-motion";
import { MapPin, Clock, Phone, MessageCircle, ExternalLink, Navigation, ShoppingBag } from "lucide-react";
import { locationInfo } from "@/data/index";
import { LocationCard } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Location() {
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-primary/5 -z-10" />
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.smooth}
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wider text-primary uppercase bg-primary/10 rounded-full">
              Visit Us
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground">
              Find Us in <span className="text-primary">Sunway Geo Mall</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Located in the heart of Bandar Sunway, we're your go-to spot for authentic Korean flavors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Info Grid */}
      <section className="container mx-auto px-4 mt-12">
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start"
        >
          {/* Left: Location Card & Address */}
          <motion.div variants={staggerItem} className="lg:col-span-5 space-y-8">
            <LocationCard location={locationInfo} />
            
            <div className="bg-card p-8 rounded-2xl border border-border shadow-sm space-y-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Navigation className="w-5 h-5 text-primary" />
                How to Get Here
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                We are located at <span className="font-semibold text-foreground">{locationInfo.unitNumber}</span> on Level 1 of Sunway Geo Mall. 
                The mall is conveniently situated {locationInfo.landmark}.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-lg">
                  <a href={locationInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                    Open in Google Maps
                  </a>
                </Button>
                <Button variant="outline" asChild className="border-primary text-primary hover:bg-primary/10 rounded-full px-8 py-6 text-lg">
                  <a href={`tel:${locationInfo.phone.replace(/\s+/g, '')}`}>
                    <Phone className="mr-2 w-5 h-5" />
                    Call Now
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right: Hours, Delivery & Socials */}
          <motion.div variants={staggerItem} className="lg:col-span-7 space-y-8">
            {/* Operating Hours */}
            <div className="bg-card p-8 rounded-2xl border border-border shadow-sm">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold">Operating Hours</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-xl bg-muted/30 border border-border">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Weekdays</p>
                  <p className="text-xl font-mono font-medium text-foreground">{locationInfo.operatingHours.weekdays}</p>
                </div>
                <div className="p-6 rounded-xl bg-primary/5 border border-primary/20">
                  <p className="text-sm font-semibold text-primary/70 uppercase tracking-wider mb-2">Weekends & Holidays</p>
                  <p className="text-xl font-mono font-medium text-primary">{locationInfo.operatingHours.weekends}</p>
                </div>
              </div>
            </div>

            {/* Grab Delivery CTA */}
            <div className="relative overflow-hidden bg-foreground text-background p-8 rounded-2xl shadow-xl">
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-primary/20 rounded-full blur-3xl opacity-50" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <ShoppingBag className="w-6 h-6 text-primary" />
                  <span className="font-bold text-primary tracking-widest uppercase">Can't make it to us?</span>
                </div>
                <h3 className="text-3xl font-bold mb-4">Get Dakgalbi Delivered</h3>
                <p className="text-background/70 mb-8 max-w-md">
                  Enjoy our authentic Korean chicken and dakgalbi from the comfort of your home. Freshly prepared and delivered hot.
                </p>
                <Button asChild className="bg-[#00B14F] hover:bg-[#009643] text-white border-none rounded-full px-8 py-6 text-lg w-full md:w-auto shadow-lg shadow-emerald-500/20">
                  <a href={locationInfo.grabLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    Order via GrabFood
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Contact Shortcuts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a 
                href={`https://wa.me/${locationInfo.whatsapp.replace(/[^0-9]/g, '')}`} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-6 bg-[#25D366]/10 border border-[#25D366]/20 rounded-xl hover:bg-[#25D366]/20 transition-colors"
              >
                <div className="p-3 bg-[#25D366] rounded-full">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#25D366]">WhatsApp Us</p>
                  <p className="font-medium">{locationInfo.whatsapp}</p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-6 bg-accent/50 border border-border rounded-xl">
                <div className="p-3 bg-primary/10 rounded-full">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary">Mall Location</p>
                  <p className="font-medium">Level 1, Sunway Geo</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Map Embed Placeholder / Section */}
      <section className="container mx-auto px-4 mt-24">
        <motion.div 
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="w-full h-[400px] bg-muted rounded-3xl border border-border overflow-hidden relative group"
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 z-10">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <MapPin className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Ready to visit?</h3>
            <p className="text-muted-foreground mb-8 max-w-sm">We're waiting for you at Sunway Geo Mall with the sizzle of fresh Dakgalbi.</p>
            <Button asChild variant="secondary" className="rounded-full">
              <a href={locationInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                Navigate via Google Maps
              </a>
            </Button>
          </div>
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--primary)_1px,_transparent_1px)] [background-size:24px_24px]" />
          </div>
        </motion.div>
      </section>
    </div>
  );
}
